import React, { useState } from 'react';
import EITopicsTab from '../control-panel/EITopicsTab';
import DocTypesTab from '../control-panel/DocTypesTab';
import CertsTab from '../control-panel/CertsTab';
import CategoriesTab from '../control-panel/CategoriesTab';
import ControlsTab from '../control-panel/ControlsTab';
import HistoryTab from '../control-panel/HistoryTab';

const UltraDenseControlPanel = ({
  show, onClose,
  layout = 'modal', setLayout,
  educatedIndividualFilters, setEducatedIndividualFilters,
  eiMinWords, setEiMinWords,
  selectedDocTypes, setSelectedDocTypes, documentTypes,
  docTypeMinWords, setDocTypeMinWords,
  certificationFilters, setCertificationFilters,
  certMinWords, setCertMinWords,
  minQualityScore, setMinQualityScore,
  minWordCount, setMinWordCount,
  exactMatch, setExactMatch,
  searchDepth, setSearchDepth,
  aggregation, setAggregation,
  selectedCategories, setSelectedCategories, categories,
  contentMinWords, setContentMinWords,
  filterHistory, getRecommendations,
  showToast
}) => {
  const [activeTab, setActiveTab] = useState('ei');
  const [floatPos, setFloatPos] = useState({ x: 60, y: 60 });
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  if (!show) return null;

  const tabs = [
    { id: 'ei', label: 'EI Topics', color: '#38bdf8' },
    { id: 'doc', label: 'Doc Types', color: '#FDE047' },
    { id: 'cert', label: 'Certs', color: '#ef4444' },
    { id: 'cat', label: 'Categories', color: '#3b82f6' },
    { id: 'ctrl', label: 'Controls', color: '#f59e0b' },
    { id: 'hist', label: 'History', color: '#10b981' }
  ];

  const layouts = [
    { id: 'modal', icon: 'fa-expand', tip: 'Modal (centered)' },
    { id: 'sidebar', icon: 'fa-columns', tip: 'Sidebar (right-docked)' },
    { id: 'floating', icon: 'fa-window-restore', tip: 'Floating (draggable)' }
  ];

  const onMouseDown = (e) => {
    if (layout !== 'floating') return;
    setDragging(true);
    setDragOffset({ x: e.clientX - floatPos.x, y: e.clientY - floatPos.y });
  };
  const onMouseMove = (e) => { if (!dragging) return; setFloatPos({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y }); };
  const onMouseUp = () => setDragging(false);

  const getWrapperStyle = () => {
    const base = { fontFamily: '"SF Mono", "Fira Code", monospace', display: 'flex', flexDirection: 'column', overflow: 'hidden' };
    if (layout === 'sidebar') return { ...base, position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, zIndex: 9999, background: '#060610', borderLeft: '1px solid rgba(253, 230, 138,0.15)', boxShadow: '-4px 0 30px rgba(253, 230, 138,0.08)' };
    if (layout === 'floating') return { ...base, position: 'fixed', left: floatPos.x, top: floatPos.y, width: 440, maxHeight: '70vh', zIndex: 9999, background: '#060610', border: '1px solid rgba(253, 230, 138,0.15)', borderRadius: 10, boxShadow: '0 8px 40px rgba(0,0,0,0.6)', resize: 'both' };
    return { ...base, background: '#060610', border: '1px solid rgba(253, 230, 138,0.15)', borderRadius: 10, width: 540, maxHeight: '85vh', boxShadow: '0 0 60px rgba(253, 230, 138,0.08)' };
  };

  const panelContent = (
    <div className="cp-shimmer" style={getWrapperStyle()} data-testid="control-panel-inner"
      role="dialog" aria-label="Control Matrix - Filter Controls"
      tabIndex={-1}
      onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
      onMouseMove={layout === 'floating' ? onMouseMove : undefined}
      onMouseUp={layout === 'floating' ? onMouseUp : undefined}
      onClick={e => e.stopPropagation()}>
      {/* Header */}
      <div style={{ background: 'rgba(253, 230, 138,0.04)', padding: '5px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(253, 230, 138,0.1)', cursor: layout === 'floating' ? 'move' : 'default', userSelect: 'none', position: 'relative', zIndex: 2 }}
        onMouseDown={onMouseDown}>
        <span className="shimmer-text" style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: 1.5 }}>CONTROL MATRIX</span>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {layouts.map(l => (
            <button key={l.id} onClick={() => setLayout && setLayout(l.id)} title={l.tip} aria-label={l.tip}
              className="btn-ocean" style={{
                background: layout === l.id ? 'rgba(253, 230, 138,0.12)' : 'transparent',
                border: layout === l.id ? '1px solid rgba(253, 230, 138,0.3)' : '1px solid transparent',
                color: layout === l.id ? '#38bdf8' : '#333',
                padding: '2px 5px', borderRadius: 4, fontSize: '0.5rem', cursor: 'pointer'
              }} data-testid={`cp-layout-${l.id}`}>
              <i className={`fas ${l.icon}`} style={{ fontSize: '0.5rem' }} />
            </button>
          ))}
          <span style={{ width: 1, height: 12, background: 'rgba(253, 230, 138,0.1)', margin: '0 3px' }} />
          <button onClick={() => {
            setSelectedDocTypes(documentTypes.map(d => d.id));
            setEducatedIndividualFilters(prev => { const n = {}; Object.keys(prev).forEach(k => n[k] = true); return n; });
            showToast && showToast('All filters enabled', 'success');
          }} className="btn-ocean" style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)', color: '#10b981', padding: '2px 7px', borderRadius: 4, fontSize: '0.5rem', cursor: 'pointer' }}
            data-testid="cp-select-all">ALL ON</button>
          <button onClick={() => {
            setSelectedDocTypes([]);
            setEducatedIndividualFilters(prev => { const n = {}; Object.keys(prev).forEach(k => n[k] = false); return n; });
            setCertificationFilters({ pearsonvue: false, government: false, advancedDegree: false });
            showToast && showToast('All filters cleared', 'info');
          }} className="btn-ocean" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', color: '#ef4444', padding: '2px 7px', borderRadius: 4, fontSize: '0.5rem', cursor: 'pointer' }}
            data-testid="cp-clear-all">ALL OFF</button>
          <button onClick={onClose} aria-label="Close control panel" style={{ background: 'none', border: 'none', color: '#555', fontSize: '0.9rem', cursor: 'pointer', padding: '0 4px' }} data-testid="cp-close">&times;</button>
        </div>
      </div>

      {/* Tabs */}
      <div role="tablist" aria-label="Filter control tabs" style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(253, 230, 138,0.08)', flexShrink: 0, position: 'relative', zIndex: 2 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} role="tab" aria-selected={activeTab === t.id} aria-label={`${t.label} filter tab`}
            className="filter-pill" style={{
              flex: 1, padding: '4px 4px', border: 'none', cursor: 'pointer',
              background: activeTab === t.id ? `${t.color}12` : 'transparent',
              color: activeTab === t.id ? t.color : '#333',
              fontSize: '0.52rem', fontWeight: activeTab === t.id ? 700 : 400,
              borderBottom: activeTab === t.id ? `2px solid ${t.color}` : '2px solid transparent',
              borderRadius: 0
            }} data-testid={`cp-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '8px 10px', overflowY: 'auto', flex: 1, position: 'relative', zIndex: 2 }}>
        {activeTab === 'ei' && <EITopicsTab educatedIndividualFilters={educatedIndividualFilters} setEducatedIndividualFilters={setEducatedIndividualFilters} eiMinWords={eiMinWords} setEiMinWords={setEiMinWords} minWordCount={minWordCount} setMinWordCount={setMinWordCount} />}
        {activeTab === 'doc' && <DocTypesTab selectedDocTypes={selectedDocTypes} setSelectedDocTypes={setSelectedDocTypes} documentTypes={documentTypes} docTypeMinWords={docTypeMinWords} setDocTypeMinWords={setDocTypeMinWords} />}
        {activeTab === 'cert' && <CertsTab certificationFilters={certificationFilters} setCertificationFilters={setCertificationFilters} certMinWords={certMinWords} setCertMinWords={setCertMinWords} contentMinWords={contentMinWords} setContentMinWords={setContentMinWords} />}
        {activeTab === 'cat' && <CategoriesTab selectedCategories={selectedCategories} setSelectedCategories={setSelectedCategories} categories={categories} />}
        {activeTab === 'ctrl' && <ControlsTab exactMatch={exactMatch} setExactMatch={setExactMatch} searchDepth={searchDepth} setSearchDepth={setSearchDepth} aggregation={aggregation} setAggregation={setAggregation} minQualityScore={minQualityScore} setMinQualityScore={setMinQualityScore} />}
        {activeTab === 'hist' && <HistoryTab filterHistory={filterHistory} getRecommendations={getRecommendations} />}
      </div>
    </div>
  );

  if (layout === 'modal') {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onClick={onClose} data-testid="ultra-dense-control-panel">
        {panelContent}
      </div>
    );
  }

  return (
    <div data-testid="ultra-dense-control-panel">
      {layout === 'sidebar' && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9998 }} onClick={onClose} />
      )}
      {panelContent}
    </div>
  );
};

export default UltraDenseControlPanel;
